import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/UIComponents';
import GoogleIcon from './ui/GoogleIcon';
import { createPageUrl } from '../utils/helpers';

/**
 * GuestGate — wraps a route's element.
 *
 *   Signed-in user  → renders {children} normally.
 *   Guest (no user) → renders a blurred decorative preview of the feature
 *                      behind a centered "sign in to unlock" upsell card.
 *
 * The real feature component is intentionally NOT mounted for guests: pages
 * like SmartScheduler/PracticeTests assume an authenticated user and read
 * Firestore with user.uid on mount, so rendering them live just to blur them
 * would throw. Instead we blur a real, pre-captured screenshot of the page
 * (feature.preview, static asset in public/guest-previews) so the guest sees
 * exactly what the feature looks like — without the crash risk. Falls back to
 * a neutral skeleton if no screenshot is provided.
 *
 * Search engines are guests too, so for an indexed page this is the page:
 * feature.heading is its h1 (matching its title) and feature.about says what
 * the tool does, below the card.
 */
export default function GuestGate({ feature, children }) {
  const { user, isGuest, signInWithGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (user) return children;
  // Auth still resolving (ProtectedRoute shows the spinner) — render nothing
  // rather than flash the upsell at a user who's actually logged in.
  if (!isGuest) return null;

  const Icon = feature?.icon || Lock;

  const handleGoogle = async () => {
    setBusy(true);
    setError('');
    try {
      await signInWithGoogle();
      // On success Firebase auth state flips, this component re-renders with
      // a user and returns {children}. On redirect-based sign-in the page
      // navigates away on its own.
    } catch (err) {
      setError(err?.message || 'Sign-in failed. Please try again.');
      setBusy(false);
    }
  };

  const goToLogin = () => {
    // Remember where they were headed so login returns them here after auth.
    navigate('/login', { state: { from: location } });
  };

  return (
    <div className="relative min-h-[calc(100vh-3.5rem)] sm:min-h-[calc(100vh-4rem)] overflow-hidden">
      {/* Blurred real screenshot of the actual page (or neutral fallback) */}
      {feature?.preview ? (
        <>
          <img
            src={feature.preview}
            alt=""
            aria-hidden="true"
            draggable="false"
            className="absolute inset-0 w-full h-full object-cover object-top select-none pointer-events-none blur-[7px] scale-105 opacity-60"
          />
          {/* Scrim so the upsell card stays legible over the screenshot */}
          <div aria-hidden="true" className="absolute inset-0 bg-base-950/55" />
        </>
      ) : (
        <div
          aria-hidden="true"
          className="absolute inset-0 select-none pointer-events-none blur-[6px] opacity-40 p-4 sm:p-6 md:p-8"
        >
          <div className="max-w-screen-xl mx-auto">
            <div className="h-9 w-56 rounded-md bg-base-800 mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-28 rounded-lg bg-base-850 border border-border" />
              ))}
            </div>
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-14 rounded-lg bg-base-850 border border-border"
                  style={{ width: `${92 - i * 6}%` }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Upsell card */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-3.5rem)] sm:min-h-[calc(100vh-4rem)] p-4">
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.25 }}
          className="w-full max-w-md bg-base-850 border border-border rounded-md shadow-floating p-6 sm:p-8 text-center"
        >
          <div className="mx-auto w-12 h-12 rounded-md bg-content-primary/10 flex items-center justify-center mb-4">
            <Icon className="w-6 h-6 text-content-muted" strokeWidth={1.5} />
          </div>

          <span className="inline-block mb-3 px-2.5 py-0.5 rounded-full bg-success-900 text-success-400 text-xs font-semibold">
            100% free · No credit card
          </span>
          <h1 className="text-lg sm:text-xl font-display font-semibold text-content-primary mb-1.5">
            {feature?.heading || (feature?.title ? `Unlock ${feature.title} for free` : 'Sign in free to continue')}
          </h1>
          <p className="text-sm text-content-secondary leading-relaxed mb-6">
            {/* Bold every "free" so it can't be missed in the blurb. */}
            {(feature?.blurb || 'Create a free account to unlock this feature, save your progress, and sync across devices.')
              .split(/(for free|free)/)
              .map((part, i) => (i % 2 ? <strong key={i} className="font-semibold text-content-primary">{part}</strong> : part))}
          </p>

          {error && (
            <div className="mb-4 p-3 bg-error-900 border border-error-500/30 rounded-md">
              <p className="text-sm text-error-400">{error}</p>
            </div>
          )}

          <div className="space-y-2.5">
            <Button
              className="w-full h-10"
              onClick={handleGoogle}
              disabled={busy}
            >
              {busy ? (
                <div className="w-4 h-4 border-2 border-base-950 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <GoogleIcon className="w-4 h-4 mr-2" />
                  Continue with Google
                </>
              )}
            </Button>
            <Button
              variant="outline"
              className="w-full h-10"
              onClick={goToLogin}
              disabled={busy}
            >
              Sign in another way
            </Button>
          </div>

          {/* Learn and the score calculator are public too; the only way out
              used to be AI Tutors, so guests never found them. */}
          <p className="mt-5 text-xs text-content-muted">
            Keep exploring as a guest:{' '}
            {/* Real links, so crawlers can follow them too. */}
            {[['AI Tutors', createPageUrl('AITutors')], ['Learn', '/study'], ['Score calculator', createPageUrl('ApScoreCalculator')]].map(([label, to], i) => (
              <React.Fragment key={to}>
                {i > 0 && ' · '}
                <Link to={to} className="underline hover:text-content-primary transition-colors">
                  {label}
                </Link>
              </React.Fragment>
            ))}
          </p>
        </motion.div>
      </div>

      {feature?.about?.length > 0 && (
        <section className="relative z-10 max-w-md mx-auto px-4 pb-16 -mt-8">
          <h2 className="text-body font-semibold text-content-primary mb-3">What you get with {feature.title}</h2>
          <ul className="space-y-2 text-sm text-content-secondary leading-relaxed list-disc pl-5">
            {feature.about.map((line) => <li key={line}>{line}</li>)}
          </ul>
          <p className="mt-4 text-sm text-content-secondary">Free for every student, with no trial and no credit card.</p>
        </section>
      )}
    </div>
  );
}
